import { FIELD_IDS } from './field-ids.generated.js';
function entry(cnToFieldId, cn) {
    const field_id = cnToFieldId[cn];
    if (!field_id) {
        throw new Error(`field-map: missing field_id for Chinese name "${cn}"，检查 field-ids.generated.ts`);
    }
    return { cn, field_id };
}
/**
 * 模板 base 上还没有、但**已经加在某个站自己的 base** 上的列。
 *
 * 读取这条链路（mapCnFieldsToTsKeys）只按中文名匹配，field_id 一个字都不用；
 * 用到 field_id 的是写入侧（fixture-writer / seed），而这些列本来就没人写。
 * 所以模板缺 id 时留空串即可 —— 关键是别像 `entry()` 那样在 import 期抛错，
 * 那会把整个应用起不来。
 *
 * 2026-09-02：电镀 / 宝石 / 颜色三列已经加到 strand 的 base，模板 base 两种 token
 * 都是 91403 Forbidden 加不上（应用和业务员对模板都没有 field:create 权限）。
 * 同一天加的「主题集合」也一样。等模板补上了，重跑 lark-cli 生成 field-ids，
 * 这里换回 entry() 就行。
 */
function optionalEntry(cnToFieldId, cn) {
    return { cn, field_id: cnToFieldId[cn] ?? '' };
}
const brandConfigCn = FIELD_IDS.brand_config.fields;
const skuCn = FIELD_IDS.sku.fields;
const brandContentCn = FIELD_IDS.brand_content.fields;
const deployConfigCn = FIELD_IDS.deploy_config.fields;
const engineConfigCn = FIELD_IDS.engine_config.fields;
const certificationCn = FIELD_IDS.certification.fields;
const testimonialCn = FIELD_IDS.testimonial.fields;
const exhibitionCn = FIELD_IDS.exhibition.fields;
export const FIELD_MAP = {
    brand_config: {
        table_id: FIELD_IDS.brand_config.table_id,
        fields: {
            brand_name: entry(brandConfigCn, '品牌名'),
            brand_slug: entry(brandConfigCn, '品牌 slug'),
            category: entry(brandConfigCn, '行业类目'),
            business_type: entry(brandConfigCn, '业务类型'),
            legal_entity: entry(brandConfigCn, '法律主体'),
            address: entry(brandConfigCn, '地址'),
            phone: entry(brandConfigCn, '电话'),
            contact_email: entry(brandConfigCn, '邮箱'),
            contact_whatsapp: entry(brandConfigCn, 'WhatsApp'),
            website_domain: entry(brandConfigCn, '官网域名'),
            staff_count: entry(brandConfigCn, '员工规模'),
            founded_year: entry(brandConfigCn, '成立年份'),
            target_markets: entry(brandConfigCn, '目标市场'),
            languages: entry(brandConfigCn, '支持语言'),
            slogan: entry(brandConfigCn, 'Slogan'),
            brand_story_short: entry(brandConfigCn, '品牌故事简介'),
            positioning_anchor: entry(brandConfigCn, '定位锚点'),
            logo: entry(brandConfigCn, 'Logo'),
            favicon: entry(brandConfigCn, 'Favicon'),
            og_image: entry(brandConfigCn, 'OG 图'),
            hero_banner_image: entry(brandConfigCn, 'Hero 主图'),
            primary_color: entry(brandConfigCn, '主色'),
            secondary_color: entry(brandConfigCn, '辅色'),
            meta_description: entry(brandConfigCn, 'Meta 描述'),
            USP_1: entry(brandConfigCn, 'USP 1'),
            USP_2: entry(brandConfigCn, 'USP 2'),
            USP_3: entry(brandConfigCn, 'USP 3'),
            USP_4: entry(brandConfigCn, 'USP 4'),
            payment_terms: entry(brandConfigCn, '付款条件'),
            shipping_methods: entry(brandConfigCn, '物流方式'),
            incoterms: entry(brandConfigCn, '贸易条款'),
            inquiry_recipient_emails: entry(brandConfigCn, '询盘接收邮箱'),
            inquiry_fields_json: entry(brandConfigCn, '询盘字段配置 JSON'),
            inquiry_recaptcha_site_key: entry(brandConfigCn, 'reCAPTCHA Site Key'),
            inquiry_recaptcha_secret_key: entry(brandConfigCn, 'reCAPTCHA Secret Key'),
            inquiry_auto_reply_text: entry(brandConfigCn, '询盘自动回复文案'),
            social_instagram: entry(brandConfigCn, 'Instagram'),
            social_facebook: entry(brandConfigCn, 'Facebook'),
            social_linkedin: entry(brandConfigCn, 'LinkedIn'),
            social_tiktok: entry(brandConfigCn, 'TikTok'),
            status: entry(brandConfigCn, '状态'),
        },
    },
    sku: {
        table_id: FIELD_IDS.sku.table_id,
        fields: {
            sku_code: entry(skuCn, 'SKU 编号'),
            brand: entry(skuCn, '关联品牌'),
            product_name_en: entry(skuCn, '英文产品名'),
            product_name_zh: entry(skuCn, '中文产品名'),
            category_tag: entry(skuCn, '品类标签'),
            description_en: entry(skuCn, '英文描述'),
            detail_long: entry(skuCn, '详细描述'),
            bullets: entry(skuCn, '卖点要点'),
            material: entry(skuCn, '材质'),
            // 详情页规格区要的三列，模板 base 上还没有（见 optionalEntry）
            plating: optionalEntry(skuCn, '电镀'),
            stone: optionalEntry(skuCn, '宝石'),
            color: optionalEntry(skuCn, '颜色'),
            dimensions: entry(skuCn, '尺寸'),
            weight_g: entry(skuCn, '重量g'),
            moq: entry(skuCn, 'MOQ'),
            price_min: entry(skuCn, '最低价'),
            price_max: entry(skuCn, '最高价'),
            price_currency: entry(skuCn, '货币'),
            lead_time_days: entry(skuCn, '生产周期天'),
            images: entry(skuCn, '产品图'),
            keywords_en: entry(skuCn, '英文关键词'),
            has_machine_cast: entry(skuCn, '含机铸工艺'),
            hs_code: entry(skuCn, 'HS Code'),
            packaging: entry(skuCn, '包装说明'),
            shipping_weight: entry(skuCn, '运输重量kg'),
            customization_options: entry(skuCn, '定制选项'),
            moq_tiers: entry(skuCn, 'MOQ 阶梯 JSON'),
            is_featured: entry(skuCn, '是否精选'),
            display_order: entry(skuCn, '显示顺序'),
            // 主题集合（多选）：按风格和卖点分，跟品类是两个维度，一款货可以进多个。
            // 2026-09-02 新加，模板 base 上还没有 → optionalEntry（见上面那段说明）
            collection_tags: optionalEntry(skuCn, '主题集合'),
            video_url: entry(skuCn, '视频 URL'),
            meta_title: entry(skuCn, 'Meta 标题'),
            meta_description: entry(skuCn, 'Meta 描述'),
            status: entry(skuCn, '状态'),
        },
    },
    brand_content: {
        table_id: FIELD_IDS.brand_content.table_id,
        fields: {
            title: entry(brandContentCn, '标题'),
            brand: entry(brandContentCn, '关联品牌'),
            content_type: entry(brandContentCn, '内容类型'),
            language: entry(brandContentCn, '语言'),
            body_markdown: entry(brandContentCn, '正文 Markdown'),
            status: entry(brandContentCn, '状态'),
        },
    },
    deploy_config: {
        table_id: FIELD_IDS.deploy_config.table_id,
        fields: {
            brand_slug: entry(deployConfigCn, '品牌 slug'),
            brand: entry(deployConfigCn, '关联品牌'),
            cloudflare_project_name: entry(deployConfigCn, 'Cloudflare 项目名'),
            dns_provider: entry(deployConfigCn, 'DNS 服务商'),
            dns_zone_id: entry(deployConfigCn, 'DNS Zone ID'),
            ga4_measurement_id: entry(deployConfigCn, 'GA4 ID'),
            gsc_verification_txt: entry(deployConfigCn, 'GSC 验证 TXT'),
            sitemap_config: entry(deployConfigCn, 'Sitemap 配置'),
            redirects: entry(deployConfigCn, '重定向规则'),
            facebook_pixel: entry(deployConfigCn, 'Facebook Pixel'),
            google_ads_tag: entry(deployConfigCn, 'Google Ads Tag'),
            bing_verification: entry(deployConfigCn, 'Bing 验证'),
            deployed_url: entry(deployConfigCn, '已部署 URL'),
            last_deployed_at: entry(deployConfigCn, '最近部署时间'),
            deploy_status: entry(deployConfigCn, '部署状态'),
        },
    },
    engine_config: {
        table_id: FIELD_IDS.engine_config.table_id,
        fields: {
            brand_slug: entry(engineConfigCn, '品牌 slug'),
            brand: entry(engineConfigCn, '关联品牌'),
            engine_workspace_id: entry(engineConfigCn, '引擎 Workspace ID'),
            publish_schedule_cron: entry(engineConfigCn, '发布计划 Cron'),
            article_categories: entry(engineConfigCn, '文章分类'),
            engine_sync_status: entry(engineConfigCn, '同步状态'),
            last_sync_at: entry(engineConfigCn, '最近同步时间'),
        },
    },
    certification: {
        table_id: FIELD_IDS.certification.table_id,
        fields: {
            name: entry(certificationCn, '认证名称'),
            brand: entry(certificationCn, '关联品牌'),
            issuer: entry(certificationCn, '颁发机构'),
            cert_number: entry(certificationCn, '证书编号'),
            issue_date: entry(certificationCn, '颁发日期'),
            expiry_date: entry(certificationCn, '过期日期'),
            certificate_file: entry(certificationCn, '证书文件'),
            display_order: entry(certificationCn, '显示顺序'),
            status: entry(certificationCn, '状态'),
        },
    },
    testimonial: {
        table_id: FIELD_IDS.testimonial.table_id,
        fields: {
            customer_name: entry(testimonialCn, '客户姓名'),
            brand: entry(testimonialCn, '关联品牌'),
            customer_title: entry(testimonialCn, '客户职位'),
            customer_company: entry(testimonialCn, '客户公司'),
            customer_country: entry(testimonialCn, '客户国家'),
            customer_photo: entry(testimonialCn, '客户照片'),
            quote: entry(testimonialCn, '证言正文'),
            rating: entry(testimonialCn, '评分'),
            source: entry(testimonialCn, '来源'),
            is_featured: entry(testimonialCn, '是否精选'),
            display_order: entry(testimonialCn, '显示顺序'),
            status: entry(testimonialCn, '状态'),
        },
    },
    exhibition: {
        table_id: FIELD_IDS.exhibition.table_id,
        fields: {
            exhibition_name: entry(exhibitionCn, '展会名称'),
            brand: entry(exhibitionCn, '关联品牌'),
            location: entry(exhibitionCn, '举办地点'),
            year: entry(exhibitionCn, '年份'),
            month: entry(exhibitionCn, '月份'),
            booth_number: entry(exhibitionCn, '展位号'),
            photos: entry(exhibitionCn, '现场照片'),
            description: entry(exhibitionCn, '展会简介'),
            is_featured: entry(exhibitionCn, '是否精选'),
            display_order: entry(exhibitionCn, '显示顺序'),
            status: entry(exhibitionCn, '状态'),
        },
    },
};
/** 中文字段名 → TS 英文键 */
export function cnToTsKey(table, cn) {
    const fields = FIELD_MAP[table].fields;
    for (const [tsKey, entry] of Object.entries(fields)) {
        if (entry.cn === cn) {
            return tsKey;
        }
    }
    return undefined;
}
/** field_id → TS 英文键 */
export function fieldIdToTsKey(table, field_id) {
    // 空 field_id 不是"没找到"的通配符：optionalEntry 的列 field_id 就是空串，
    // 不挡一下的话拿空串来查会随机命中其中一列。
    if (!field_id)
        return undefined;
    const fields = FIELD_MAP[table].fields;
    for (const [tsKey, entry] of Object.entries(fields)) {
        if (entry.field_id === field_id) {
            return tsKey;
        }
    }
    return undefined;
}
//# sourceMappingURL=field-map.js.map